const CLIENT_ID = process.env.REACT_APP_CLIENT_ID || '9fbqtoeasdj4qo8gkk3neb4jd';
const USER_POOL_ID = process.env.REACT_APP_USER_POOL_ID || '';

// Cross-subdomain auth cookie for SSO with Growth Tools (gtools.bcorex.ninja)
const AUTH_COOKIE_NAME = 'bcore_id_token';
const AUTH_COOKIE_DOMAIN = process.env.REACT_APP_SITE_DOMAIN
  ? '.' + process.env.REACT_APP_SITE_DOMAIN.split('.').slice(-2).join('.')
  : '.bcorex.ninja';

function setAuthCookie(idToken) {
  const payload = parseJwt(idToken);
  const maxAge = payload?.exp ? payload.exp - Math.floor(Date.now() / 1000) : 3600;
  document.cookie = `${AUTH_COOKIE_NAME}=${idToken}; domain=${AUTH_COOKIE_DOMAIN}; path=/; secure; samesite=lax; max-age=${maxAge}`;
}

function clearAuthCookie() {
  document.cookie = `${AUTH_COOKIE_NAME}=; domain=${AUTH_COOKIE_DOMAIN}; path=/; secure; samesite=lax; max-age=0`;
}

// Derive region from User Pool ID (format: us-east-1_xxxxxxxxx)
const COGNITO_REGION = USER_POOL_ID.split('_')[0] || 'us-east-1';
const COGNITO_ENDPOINT = `https://cognito-idp.${COGNITO_REGION}.amazonaws.com`;

// Refresh tokens 2 minutes before they expire
const REFRESH_BUFFER_SECONDS = 120;

// Prevent concurrent refresh calls
let refreshPromise = null;

/**
 * Parse a JWT token and return the payload.
 */
function parseJwt(token) {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch {
    return null;
  }
}

/**
 * Check if a JWT token is expired or will expire within the buffer window.
 */
function isTokenExpiring(token) {
  const payload = parseJwt(token);
  if (!payload || !payload.exp) return true;
  const now = Math.floor(Date.now() / 1000);
  return payload.exp < now + REFRESH_BUFFER_SECONDS;
}

/**
 * Check if a JWT token is fully expired (past expiry, not just in buffer).
 */
function isTokenExpired(token) {
  const payload = parseJwt(token);
  if (!payload || !payload.exp) return true;
  const now = Math.floor(Date.now() / 1000);
  return payload.exp < now;
}

/**
 * Get stored tokens from localStorage using Cognito key format.
 * Returns { idToken, accessToken, refreshToken, username } or null if not found.
 * Does NOT clear tokens on expiry — callers should use getAuthToken() which handles refresh.
 */
export function getStoredTokens() {
  const prefix = `CognitoIdentityServiceProvider.${CLIENT_ID}`;
  const lastAuthUser = localStorage.getItem(`${prefix}.LastAuthUser`);
  if (!lastAuthUser) return null;

  const idToken = localStorage.getItem(`${prefix}.${lastAuthUser}.idToken`);
  const accessToken = localStorage.getItem(`${prefix}.${lastAuthUser}.accessToken`);
  const refreshToken = localStorage.getItem(`${prefix}.${lastAuthUser}.refreshToken`);

  if (!idToken) return null;

  return { idToken, accessToken, refreshToken, username: lastAuthUser };
}

/**
 * Store new tokens in localStorage using Cognito key format.
 */
function storeTokens(username, idToken, accessToken) {
  const prefix = `CognitoIdentityServiceProvider.${CLIENT_ID}`;
  localStorage.setItem(`${prefix}.${username}.idToken`, idToken);
  localStorage.setItem(`${prefix}.${username}.accessToken`, accessToken);
  // Cognito does not return a new refresh token on refresh — keep the existing one
  setAuthCookie(idToken);
}

/**
 * Clear all stored tokens from localStorage.
 */
export function clearStoredTokens() {
  const prefix = `CognitoIdentityServiceProvider.${CLIENT_ID}`;
  const lastAuthUser = localStorage.getItem(`${prefix}.LastAuthUser`);
  if (lastAuthUser) {
    localStorage.removeItem(`${prefix}.${lastAuthUser}.idToken`);
    localStorage.removeItem(`${prefix}.${lastAuthUser}.accessToken`);
    localStorage.removeItem(`${prefix}.${lastAuthUser}.refreshToken`);
  }
  localStorage.removeItem(`${prefix}.LastAuthUser`);
  clearAuthCookie();
}

/**
 * Check if stored tokens exist (quick check without full validation).
 * Used to skip expensive getUser calls when no tokens exist.
 */
export function hasStoredTokens() {
  const prefix = `CognitoIdentityServiceProvider.${CLIENT_ID}`;
  const lastAuthUser = localStorage.getItem(`${prefix}.LastAuthUser`);
  if (!lastAuthUser) return false;
  const idToken = localStorage.getItem(`${prefix}.${lastAuthUser}.idToken`);
  return !!idToken;
}

/**
 * Refresh the session using the stored refresh token.
 * Calls Cognito InitiateAuth with REFRESH_TOKEN_AUTH flow.
 * Returns new tokens or null if refresh fails.
 *
 * NIST 800-171 3.5.4 — replay-resistant authentication.
 */
async function refreshSession() {
  const tokens = getStoredTokens();
  if (!tokens || !tokens.refreshToken) return null;

  try {
    const response = await fetch(COGNITO_ENDPOINT, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-amz-json-1.1',
        'X-Amz-Target': 'AWSCognitoIdentityProviderService.InitiateAuth',
      },
      body: JSON.stringify({
        AuthFlow: 'REFRESH_TOKEN_AUTH',
        ClientId: CLIENT_ID,
        AuthParameters: {
          REFRESH_TOKEN: tokens.refreshToken,
        },
      }),
    });

    if (!response.ok) {
      console.warn('Token refresh failed:', response.status);
      clearStoredTokens();
      return null;
    }

    const data = await response.json();
    const result = data.AuthenticationResult;

    if (!result || !result.IdToken || !result.AccessToken) {
      console.warn('Token refresh returned incomplete result');
      clearStoredTokens();
      return null;
    }

    // Store refreshed tokens
    storeTokens(tokens.username, result.IdToken, result.AccessToken);

    return {
      idToken: result.IdToken,
      accessToken: result.AccessToken,
      refreshToken: tokens.refreshToken,
      username: tokens.username,
    };
  } catch (err) {
    console.error('Token refresh error:', err);
    clearStoredTokens();
    return null;
  }
}

/**
 * Get the ID token string for use in API Authorization headers.
 * Automatically refreshes the token if it is expired or about to expire.
 * Returns null if no valid session (user must re-login).
 */
export async function getAuthToken() {
  const tokens = getStoredTokens();
  if (!tokens) return null;

  // Token is still fresh — return it
  if (!isTokenExpiring(tokens.idToken)) {
    return tokens.idToken;
  }

  // Token is expiring or expired — try to refresh
  // Use a shared promise to prevent concurrent refresh calls
  if (!refreshPromise) {
    refreshPromise = refreshSession().finally(() => {
      refreshPromise = null;
    });
  }

  const refreshed = await refreshPromise;
  if (refreshed) {
    return refreshed.idToken;
  }

  // Refresh failed — no valid session
  return null;
}

/**
 * Get the current authenticated user info.
 * Returns { email, groups } or null.
 */
export async function getUser() {
  const tokens = getStoredTokens();
  if (!tokens) return null;

  let idToken = tokens.idToken;

  // If the ID token is fully expired, try to refresh
  if (isTokenExpired(idToken)) {
    const refreshed = await refreshSession();
    if (!refreshed) return null;
    idToken = refreshed.idToken;
  }

  const payload = parseJwt(idToken);
  if (!payload) return null;

  const email = payload.email || payload['cognito:username'] || 'unknown';
  const groups = payload['cognito:groups'] || [];

  return { email, groups };
}

/**
 * Check if the current user belongs to the "admin" Cognito group.
 */
export async function isAdmin() {
  const user = await getUser();
  if (!user) return false;
  return Array.isArray(user.groups) && user.groups.includes('admin');
}

/**
 * Sign out the current user by clearing stored tokens.
 */
export async function logout() {
  clearStoredTokens();
}
